/*
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--' 
         `---'                                                                                                      
*/
import { BaseService, HttpResponse } from '../../service/base';

/** Holds the DTO information for a camera entity from the server.
 *
 */
export interface CameraDTO {
    id: string; /// UUID identifier of the camera meta data
    filename: string; /// Name of file on server ( not really useful? )
    asset: string; /// UUID of the asset the picture was from.
    timestamp: number; /// UTC timestamp of photo
    latitude: number; /// Latitude location of photo
    longitude: number; /// Longitude location of photo
    imageBytes: string; /// JPEG data bytes.
}

/** Camera Service Layer to handle communications to and from the server.
 *
 */
export class CameraService extends BaseService {
    /// Chache of image data promises. Key on the UUID
    public imageCache: Map<string, Promise<CameraDTO>>;

    constructor() {
        super();
        this.imageCache = new Map<string, Promise<CameraDTO>>();
    }

    /** Called once the basic system is in place.
     * Do any pre-application loading from the server here.
     */
    public async init() {
        return new Promise((resolve, _reject) => {
            console.log('Camera Service Loaded');
            resolve('Loaded');
        });
    }

    async getImagesByAsset(assetId: string, startUTC: number, endUTC: number): Promise<HttpResponse<CameraDTO[]>> {
        const response = await this.get<CameraDTO[]>(this.url + '/api/' + this.orgid + '/camera/date?assetId=' + assetId + 'start=' + startUTC + '&end=' + endUTC);
        return response;
    }

    async getLatest(): Promise<HttpResponse<CameraDTO[]>> {
        const response = await this.get<CameraDTO[]>(this.url + '/api/' + this.orgid + '/camera/latest');
        return response;
    }

    /** Gets data from the server based on a date range.
     *
     * @param start The start time (UTC)
     * @param end  The end time (UTC)
     * @returns The promise with the response data.
     */
    async getInDateRange(start: number, end: number): Promise<HttpResponse<CameraDTO[]>> {
        const response = await this.get<CameraDTO[]>(this.url + '/api/' + this.orgid + '/camera?start=' + start + '&end=' + end);
        return response;
    }

    /** Request camera data from the server.
     *
     * @param id The UUID of the camera entity object.
     * @return The promise for the json parse of the response.
     */
    getCameraData(id: string): Promise<CameraDTO> {
        return fetch(this.url + '/api/' + this.orgid + '/camera/' + id, {
            method: 'GET',
            headers: this.headers(),
        }).then((response) => {
            return response.json();
        });
    }

    /** Get image data but check the cache first.
     *  Will check to see if the image ( UUID ) is in the cache first and return that.
     *  If not in the cache will make request back out to the server for the data.
     *
     *  @param id The UUID of the image
     *  @return The promise of json parse of the data from the server (or cache)
     */
    public cachedCameraData(id: string): Promise<CameraDTO> {
        // Check if the data for the given id is already cached

        const response = this.imageCache.get(id);
        if (response != undefined) {
            return response;
        } else {
            const promise = this.getCameraData(id);
            this.imageCache.set(id, promise);
            return promise;
        }
    }
}
